import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { FiRefreshCw, FiPackage, FiSlash, FiDollarSign } from "react-icons/fi";
import { Modal } from "../../Components/Modal";
import { usePermissions } from "../../Auth/PermissionsContext";
import { shopClient, eurCents, fmtDate, type ShopCoreReturnRow } from "./shopClient";

type Tone = "emerald" | "amber" | "slate" | "rose";

const TONE_CLASSES: Record<Tone, string> = {
	emerald: "bg-emerald-50 text-emerald-700",
	amber: "bg-amber-50 text-amber-700",
	slate: "bg-slate-100 text-slate-600",
	rose: "bg-rose-50 text-rose-600",
};

interface StatusMeta {
	tone: Tone;
	label: string;
}

const RETURN_STATUS: Record<string, StatusMeta> = {
	AwaitingReturn: { tone: "amber", label: "En attente" },
	AwaitingCoreFirst: { tone: "amber", label: "Pièce requise avant expédition" },
	Received: { tone: "slate", label: "Reçue" },
	Refunded: { tone: "emerald", label: "Remboursée" },
	Forfeited: { tone: "rose", label: "Non retournée" },
};

function returnStatusBadge(status: string): React.ReactElement {
	const meta = RETURN_STATUS[status] ?? { tone: "slate" as Tone, label: status };
	return (
		<span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${TONE_CLASSES[meta.tone]}`}>
			{meta.label}
		</span>
	);
}

const STATUS_FILTERS: Array<{ value: string; label: string }> = [
	{ value: "", label: "Tous" },
	{ value: "AwaitingReturn", label: "En attente" },
	{ value: "AwaitingCoreFirst", label: "Ancienne pièce d'abord" },
	{ value: "Received", label: "Reçue" },
	{ value: "Refunded", label: "Remboursée" },
	{ value: "Forfeited", label: "Non retournée" },
];

export default function CoreReturns(): React.ReactElement {
	const { can } = usePermissions();
	const canWrite = can("feature:returns:write");

	const [rows, setRows] = useState<ShopCoreReturnRow[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [status, setStatus] = useState("");
	const [busyId, setBusyId] = useState<string | null>(null);
	const [refundTarget, setRefundTarget] = useState<ShopCoreReturnRow | null>(null);
	const [refunding, setRefunding] = useState(false);

	const load = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const r = await shopClient.returns.list(status || undefined);
			setRows(r.items);
		} catch (e: any) {
			setError(e?.message ?? "Erreur");
			setRows([]);
		} finally {
			setLoading(false);
		}
	}, [status]);

	useEffect(() => {
		void load();
	}, [load]);

	const receive = useCallback(
		async (row: ShopCoreReturnRow) => {
			setBusyId(row.id);
			try {
				await shopClient.returns.receive(row.id);
				toast.success("Consigne marquée comme reçue.");
				await load();
			} catch (e: any) {
				toast.error(e?.message ?? "Erreur");
			} finally {
				setBusyId(null);
			}
		},
		[load]
	);

	const forfeit = useCallback(
		async (row: ShopCoreReturnRow) => {
			setBusyId(row.id);
			try {
				await shopClient.returns.forfeit(row.id);
				toast.success("Consigne marquée comme non retournée.");
				await load();
			} catch (e: any) {
				toast.error(e?.message ?? "Erreur");
			} finally {
				setBusyId(null);
			}
		},
		[load]
	);

	const confirmRefund = useCallback(async () => {
		if (!refundTarget) return;
		setRefunding(true);
		try {
			await shopClient.returns.refund(refundTarget.id);
			toast.success("Caution remboursée.");
			setRefundTarget(null);
			await load();
		} catch (e: any) {
			toast.error(e?.message ?? "Erreur");
		} finally {
			setRefunding(false);
		}
	}, [refundTarget, load]);

	const hasRows = useMemo(() => rows.length > 0, [rows]);

	return (
		<div className="min-h-full p-6">
			<div className="mx-auto w-full max-w-6xl space-y-6">
				<div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
					<div>
						<p className="text-xs uppercase tracking-widest text-slate-500">Boutique</p>
						<h1 className="text-2xl font-semibold text-slate-900">Consignes &amp; retours</h1>
						<p className="mt-1 text-sm text-slate-500">
							Suivi des cautions carcasse (échange standard)
						</p>
					</div>
					<button
						type="button"
						onClick={() => void load()}
						disabled={loading}
						className="ml-auto inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-400 disabled:opacity-50"
					>
						<FiRefreshCw className={loading ? "animate-spin" : undefined} />
						Actualiser
					</button>
				</div>

				<div className="rounded-2xl border border-slate-100 bg-white shadow-sm">
					<div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-3">
						<h2 className="text-sm font-semibold text-slate-700">Consignes</h2>
						<select
							value={status}
							onChange={(e) => setStatus(e.target.value)}
							className="ml-auto rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
						>
							{STATUS_FILTERS.map((f) => (
								<option key={f.value} value={f.value}>
									{f.label}
								</option>
							))}
						</select>
					</div>

					{error && (
						<div className="px-5 py-4">
							<div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
								{error}
							</div>
						</div>
					)}

					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-slate-100 text-sm">
							<thead className="bg-slate-50">
								<tr>
									<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">
										Commande
									</th>
									<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">
										Client
									</th>
									<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">
										Produit
									</th>
									<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">
										Montant
									</th>
									<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">
										Statut
									</th>
									<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">
										Créé
									</th>
									<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">
										Actions
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-slate-100">
								{loading && !hasRows ? (
									<tr>
										<td colSpan={7} className="px-5 py-10 text-center text-slate-400">
											Chargement…
										</td>
									</tr>
								) : !hasRows ? (
									<tr>
										<td colSpan={7} className="px-5 py-10 text-center text-slate-400">
											Aucune consigne
										</td>
									</tr>
								) : (
									rows.map((row) => {
										const busy = busyId === row.id;
										return (
											<tr key={row.id} className="hover:bg-slate-50">
												<td className="px-5 py-3">
													<Link
														to={`/orders/${row.orderId}`}
														className="font-medium text-slate-900 hover:text-slate-600"
													>
														#{row.orderId.slice(0, 8).toUpperCase()}
													</Link>
												</td>
												<td className="px-5 py-3 text-slate-700">{row.customerEmail || "—"}</td>
												<td className="px-5 py-3 text-slate-700">{row.productTitle || "—"}</td>
												<td className="px-5 py-3 font-medium text-slate-900">
											{row.refundAmountCents > 0 ? eurCents(row.refundAmountCents, row.currency) : "Aucune consigne"}
												</td>
												<td className="px-5 py-3">{returnStatusBadge(row.status)}</td>
												<td className="px-5 py-3 text-slate-500">{fmtDate(row.createdAt)}</td>
												<td className="px-5 py-3">
													{!canWrite ? (
														<span className="text-slate-400">—</span>
											) : row.status === "AwaitingReturn" || row.status === "AwaitingCoreFirst" ? (
												<div className="flex flex-wrap items-center gap-2">
													<button
														type="button"
														onClick={() => void receive(row)}
														disabled={busy}
														className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-slate-400 disabled:opacity-50"
													>
														<FiPackage />
														Marquer reçu
													</button>
													{row.refundAmountCents > 0 ? (
														<button
															type="button"
															onClick={() => setRefundTarget(row)}
															disabled={busy}
															className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
														>
															<FiDollarSign />
															Rembourser
														</button>
													) : null}
															<button
																type="button"
																onClick={() => void forfeit(row)}
																disabled={busy}
																className="inline-flex items-center gap-2 rounded-md bg-rose-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-700 disabled:opacity-50"
															>
																<FiSlash />
																Non retournée
															</button>
														</div>
											) : row.status === "Received" && row.refundAmountCents > 0 ? (
														<button
															type="button"
															onClick={() => setRefundTarget(row)}
															disabled={busy}
															className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
														>
															<FiDollarSign />
															Rembourser
														</button>
													) : (
														<span className="text-slate-400">—</span>
													)}
												</td>
											</tr>
										);
									})
								)}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			<Modal show={refundTarget !== null} onHide={() => setRefundTarget(null)} size="md" centered>
				<Modal.Header closeButton>
					<Modal.Title>Rembourser la caution</Modal.Title>
				</Modal.Header>
				<Modal.Body className="space-y-4">
					<p className="text-sm text-slate-600">
						Confirmez le remboursement de la caution carcasse pour la commande{" "}
						<span className="font-medium text-slate-900">
							#{refundTarget ? refundTarget.orderId.slice(0, 8).toUpperCase() : ""}
						</span>
						.
					</p>
					{refundTarget && (
						<div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
							<div className="flex items-center justify-between">
								<span className="text-slate-500">Produit</span>
								<span className="font-medium text-slate-800">{refundTarget.productTitle || "—"}</span>
							</div>
							<div className="mt-2 flex items-center justify-between">
								<span className="text-slate-500">Montant</span>
								<span className="font-semibold text-slate-900">
									{eurCents(refundTarget.refundAmountCents, refundTarget.currency)}
								</span>
							</div>
						</div>
					)}
					<p className="text-xs text-slate-400">
						Cette action déclenche un remboursement Stripe et ne peut pas être annulée.
					</p>
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						onClick={() => setRefundTarget(null)}
						disabled={refunding}
						className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-400 disabled:opacity-50"
					>
						Annuler
					</button>
					<button
						type="button"
						onClick={() => void confirmRefund()}
						disabled={refunding}
						className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
					>
						<FiDollarSign />
						{refunding ? "Remboursement…" : "Confirmer le remboursement"}
					</button>
				</Modal.Footer>
			</Modal>
		</div>
	);
}
